import React, { useState, useEffect } from 'react';
import { createAttribute, updateAttribute } from '../../services/attributesService';

const AttributeForm = ({ isOpen, onClose, attribute = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'text',
    is_required: false,
    options: [],
    is_active: true,
  });
  const [optionsText, setOptionsText] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (attribute) {
      // Modo edición
      setFormData({
        code: attribute.code || '',
        name: attribute.name || '',
        type: attribute.type || 'text',
        is_required: attribute.is_required !== undefined ? attribute.is_required : false,
        options: attribute.options || [],
        is_active: attribute.is_active !== undefined ? attribute.is_active : true,
      });
      setOptionsText(attribute.options && Array.isArray(attribute.options) 
        ? attribute.options.join(', ') 
        : '');
    } else {
      // Modo creación
      setFormData({
        code: '',
        name: '',
        type: 'text',
        is_required: false,
        options: [],
        is_active: true,
      });
      setOptionsText('');
    }
    setErrors({});
  }, [attribute, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Para el código: alfanumérico, máximo 50 caracteres, convertir a mayúsculas
    if (name === 'code') {
      processedValue = value.replace(/[^0-9A-Za-z_]/g, '').toUpperCase();
      if (processedValue.length > 50) {
        processedValue = processedValue.slice(0, 50);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));
    
    // Si cambia el tipo y no es 'select', limpiar opciones
    if (name === 'type' && processedValue !== 'select') {
      setFormData(prev => ({
        ...prev,
        options: [],
      }));
      setOptionsText('');
    }
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleOptionsChange = (e) => {
    const text = e.target.value;
    setOptionsText(text);
    
    // Convertir texto separado por comas en array
    const optionsArray = text
      .split(',')
      .map(opt => opt.trim())
      .filter(opt => opt.length > 0);
    
    setFormData(prev => ({
      ...prev,
      options: optionsArray,
    }));
    
    // Limpiar error de opciones
    if (errors.options) {
      setErrors(prev => ({
        ...prev,
        options: null,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'El código es obligatorio';
    } else if (formData.code.length > 50) {
      newErrors.code = 'El código no puede tener más de 50 caracteres';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if (formData.name.length > 100) {
      newErrors.name = 'El nombre no puede tener más de 100 caracteres';
    }

    // Si es tipo 'select', debe tener opciones
    if (formData.type === 'select' && (!formData.options || formData.options.length === 0)) {
      newErrors.options = 'Las opciones son obligatorias para atributos de tipo selección';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      const submitData = {
        ...formData,
        // Solo enviar opciones si es tipo select
        options: formData.type === 'select' ? formData.options : null,
      };
      
      if (attribute) {
        // Actualizar
        response = await updateAttribute(attribute.id, submitData);
      } else {
        // Crear
        response = await createAttribute(submitData);
      }

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        // Manejar errores del servidor
        if (response.errors) {
          setErrors(response.errors);
        } else {
          setErrors({ submit: response.message || 'Error al guardar el atributo' });
        }
      }
    } catch (error) {
      console.error('Error al guardar atributo:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Error al guardar el atributo' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {attribute ? 'Editar Atributo' : 'Nuevo Atributo'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {errors.submit && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              {errors.submit}
            </div>
          )}

          {/* Código */}
          <div className="mb-4">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              maxLength={50}
              readOnly={!!attribute} // Solo lectura en modo edición
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.code
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              } ${attribute ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Ej: COLOR, TALLA"
            />
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {attribute ? 'El código no se puede modificar' : 'Máximo 50 caracteres alfanuméricos'}
            </p>
          </div>

          {/* Nombre */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={100}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: Color, Talla"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Máximo 100 caracteres</p>
          </div>

          {/* Tipo */}
          <div className="mb-4">
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo <span className="text-red-500">*</span>
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.type
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
            >
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="select">Selección</option>
              <option value="boolean">Booleano</option>
              <option value="date">Fecha</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type}</p>
            )}
          </div>

          {/* Opciones (solo si es tipo select) */}
          {formData.type === 'select' && (
            <div className="mb-4">
              <label htmlFor="options" className="block text-sm font-medium text-gray-700 mb-1">
                Opciones <span className="text-red-500">*</span>
              </label>
              <textarea
                id="options"
                name="options"
                value={optionsText}
                onChange={handleOptionsChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.options
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
                placeholder="Separar opciones por comas. Ej: Rojo, Azul, Verde, Negro"
              />
              {errors.options && (
                <p className="mt-1 text-sm text-red-600">{errors.options}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Separar cada opción con una coma. Se crearán {formData.options.length} opciones.
              </p>
            </div>
          )}

          {/* Requerido */}
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_required"
                checked={formData.is_required}
                onChange={handleChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Atributo obligatorio</span>
            </label>
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Atributo activo</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : attribute ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttributeForm;

